export type TimeUnit = "hours" | "minutes";

const STORAGE_KEY = "time-input-unit-preference";

/**
 * Converts a display value in the given unit to decimal hours for storage.
 */
export function toDecimalHours(value: number, unit: TimeUnit): number {
  if (unit === "minutes") {
    return value / 60;
  }
  return value;
}

/**
 * Converts decimal hours to the display value in the chosen unit.
 */
export function fromDecimalHours(hours: number, unit: TimeUnit): number {
  if (unit === "minutes") {
    return hours * 60;
  }
  return hours;
}

/**
 * Formats decimal hours as a human-readable string.
 * e.g., 1.5 → "1 hour 30 minutes", 0.25 → "15 minutes", 2 → "2 hours"
 */
export function formatHumanReadableTime(hours: number): string {
  if (!hours || hours <= 0) return "";
  const totalMinutes = Math.round(hours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;

  if (h === 0) {
    return `${m} minute${m !== 1 ? "s" : ""}`;
  }
  if (m === 0) {
    return `${h} hour${h !== 1 ? "s" : ""}`;
  }
  return `${h} hour${h !== 1 ? "s" : ""} ${m} minute${m !== 1 ? "s" : ""}`;
}

/**
 * Formats decimal hours in compact form for display.
 * e.g., 1.5 → "1h 30m", 0.25 → "15m", 2 → "2h", 0 → "0h"
 */
export function formatCompactTime(hours: number): string {
  if (hours === null || hours === undefined || isNaN(hours)) return "0h";
  if (hours === 0) return "0h";
  const totalMinutes = Math.round(hours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;

  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/**
 * Rounds decimal hours to the nearest quarter hour (0.25).
 */
export function roundToQuarterHour(hours: number): number {
  return Math.round(hours * 4) / 4;
}

/**
 * Returns input constraints (min, max, step) for a given unit.
 */
export function getInputConstraints(
  unit: TimeUnit,
  { minHours = 0.25, maxHours = 24 }: { minHours?: number; maxHours?: number } = {}
): { min: number; max: number; step: number } {
  if (unit === "minutes") {
    return {
      min: minHours * 60,
      max: maxHours * 60,
      step: 15,
    };
  }
  return {
    min: minHours,
    max: maxHours,
    step: 0.25,
  };
}

/**
 * Get persisted unit preference from localStorage.
 */
export function getStoredUnitPreference(): TimeUnit {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "hours" || stored === "minutes") return stored;
  } catch {}
  return "hours";
}

/**
 * Persist unit preference to localStorage.
 */
export function setStoredUnitPreference(unit: TimeUnit): void {
  try {
    localStorage.setItem(STORAGE_KEY, unit);
  } catch {}
}
